import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Injector,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {L10nTranslationService} from 'angular-l10n';
import {nanoid} from 'nanoid';
import {AsyncSubject, BehaviorSubject, EMPTY, Observable, Subject} from 'rxjs';
import {finalize, map, switchMap, switchMapTo, takeUntil, tap} from 'rxjs/operators';
import {IS_ORDER_REPLACE_DISABLED} from '../../common/constants';
import {isOrderActive} from '../../common/is-order-active';
import {waitPreviousProcessLast} from '../../common/rxjs/wait-previous-process-last';
import {AccountService} from '../../components/accounts';
import {destroy$} from '../../components/destroyable';
import {FloatCellDefinition} from '../../components/float-layout';
import {Widget} from '../../components/layout/widget/widget';
import {WidgetDefinition} from '../../components/layout/widget/widget-definition';
import {
    getOrderQtyAssetType,
    getOrderQtyCorrectedSecurityFormats,
    getOrderQtyCurrency,
    getOrderQtyMultiCurrencyInfoForOrder,
    getOrderQtyMultiCurrencyInfoForPosition,
    getQtyFromOrder,
    OrderQtyMultiCurrencyInfo
} from '../../components/order-qty-multi-currency';
import {Position} from '../../components/position';
import {DisplayPositionFactory} from '../../components/position/display-position.factory';
import {SymbolDecimalsInputParams, SymbolDecimalsService, SymbolFormats} from '../../components/symbol-decimals';
import {TradingViewChartFactory, TradingViewChartStyleVariablesAccessor} from '../../components/trading-view-chart';
import {
    ChartingLibraryWidgetOptions,
    CrossHairMovedEventParams,
    IChartingLibraryWidget,
    IOrderLineAdapter,
    IPositionLineAdapter
} from '../../components/trading-view-chart/charting-library';
import {ResolutionString, Timezone} from '../../components/trading-view-chart/charting-library/datafeed-api';
import {Language} from '../../components/translate/language-decorator';
import {AssetType} from '../../enums/asset-type';
import {OrderTransactionType} from '../../enums/order-transaction-type';
import {OrdType} from '../../enums/order-type';
import {Side} from '../../enums/side';
import {Order, ReplaceOrCancelOrder} from '../../models/order';
import {Security} from '../../models/security';
import {formatSecurityKey, isSecurityKeysEqual, SecurityKey} from '../../models/security-key';
import {FloatLayoutService} from '../../pages/dashboard/float-layout/float-layout.service';
import {CellSizeObserverService} from '../../pages/dashboard/layout/cell/cell-size-observer.service';
import {AppConfigService} from '../../services/config.service';
import {OrderPlacingService} from '../components/order-placing';
import {OrderCancelingParams, OrderCreationParams} from '../components/order-placing/order-placing-base.service';
import {getAccountBaseByNewOrderParams} from '../components/order-placing/order-placing-utils';
import {getSquashedPositionsStream, PositionsStreamingService} from '../components/positions-streaming';
import {ORDER_DETAILS_WIDGET_DESCRIPTION} from '../order-details-widget';
import {OrderDetailsWidgetSettingsDef} from '../order-details-widget/settings';
import {DisplayOrder, DisplayOrderFactory} from '../orders-widget/display-order.factory';
import {
    handleOrderListEvents,
    ORDER_LIST_DISPLAY_ORDER_FACTORY,
    OrderListEventType,
    OrderListService
} from '../orders-widget/order-list.service';
import {getTimeZoneNameForChart} from './available-timezones';
import {ChartWidget} from './chart-widget';
import {ChartDatafeed} from './datafeed';
import {ChartDataPulseProvider} from './datafeed/chart-data-pulse-provider';
import {ChartHistoryProvider} from './datafeed/chart-history-provider';
import {ChartSecurityService} from './datafeed/chart-security.service';
import {updateChartSettingsByState} from './settings/chart-common-settings';
import NumberFormat = Intl.NumberFormat;

interface OrderLineRef {
    chartLine: IOrderLineAdapter;
    order: Order;
}

interface ChartLineStyles {
    lineBodyBackgroundColor: string;
    lineBodyBorderColor: string;
    lineColorBuy: string;
    lineTextColorBuy: string;
    lineColorSell: string;
    lineTextColorSell: string;
}

const CHART_LINE_STYLE_VARIABLE_NAMES: Array<keyof ChartLineStyles> = [
    'lineBodyBackgroundColor',
    'lineBodyBorderColor',
    'lineColorBuy',
    'lineTextColorBuy',
    'lineColorSell',
    'lineTextColorSell'
];

const CHART_LINE_COLOR_VARIABLE_BY_SIDE: { [side in Side]: keyof ChartLineStyles } = {
    BUY:  'lineColorBuy',
    SELL: 'lineColorSell'
};
const CHART_LINE_TEXT_COLOR_VARIABLE_BY_SIDE: { [side in Side]: keyof ChartLineStyles } = {
    BUY:  'lineTextColorBuy',
    SELL: 'lineTextColorSell'
};

const TOP_TOOLBAR_HIDING_CLASS = '-top-toolbar-hiding';

const TOTAL_PNL_LABEL: string = 'components.widgets.chart-widget.total_pnl';

@Component({
    selector: 'chart-widget',
    templateUrl: './chart-widget.component.html',
    styleUrls: ['./chart-widget.component.scss'],
    providers: [
        TradingViewChartFactory,
        // OrderListService, // [HED-930] Not used in DEX
        {
            provide: ORDER_LIST_DISPLAY_ORDER_FACTORY,
            useClass: DisplayOrderFactory
        },
        /**
         * Provide Datafeed and DataPulse here
         * in order to separate similar subscriptions from different chart widgets.
         * Provide HistoryProvider here in order to support destroy$ in this service.
         */
        ChartDatafeed,
        ChartDataPulseProvider,
        ChartHistoryProvider
        // OrderPlacingService // [HED-930] Not used in DEX
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChartWidgetComponent implements AfterViewInit, OnDestroy, OnInit {

    public chartId = nanoid();

    public readonly chartReady$ = new AsyncSubject<boolean>();

    public readonly destroy$ = destroy$();

    public readonly chartInstance$ = new BehaviorSubject<IChartingLibraryWidget>(null);

    @ViewChild('confirmModalAnchor', {read: ViewContainerRef, static: true})
    public confirmModalAnchorViewContainerRef: ViewContainerRef;

    @ViewChild('chartContainer', {static: true})
    public chartContainerElementRef: ElementRef;

    // Tells what the TV lib is fully loaded, including custom styles
    public isChartReady: boolean = false;

    private chartInstance: IChartingLibraryWidget;

    private chartStyleVariables: TradingViewChartStyleVariablesAccessor;

    private readonly orderLineMapByClOrdId = new Map<string, OrderLineRef>();

    private readonly crossHairMoved$ = new Subject<CrossHairMovedEventParams>();

    private security: Security;

    private securityFormats: SymbolFormats;
    private securityInputParams: SymbolDecimalsInputParams;

    private positionLine: IPositionLineAdapter;
    private positionLabelText: string;
    private formattedTotalPnL: string;

    private chartLineStyles: ChartLineStyles;

    private chartIFrame: HTMLIFrameElement;
    private chartDocument: Document;
    private readonly resize$ = this.cellSizeObserverService.resize$;
    private isTopToolbarOverflow: boolean = false;

    private readonly updateChartSymbolBySettings$ = new Subject<void>();

    private readonly widget = inject(Widget) as ChartWidget;

    constructor(
        private injector: Injector,

        @Optional() // [HED-930] Not used in DEX
        private orderListService: OrderListService<DisplayOrder>,

        @Optional() // [HED-930] Not used in DEX
        private positionsStreamingService: PositionsStreamingService,

        public translationService: L10nTranslationService,
        private floatLayoutService: FloatLayoutService,
        private appConfigService: AppConfigService,
        private symbolDecimalsService: SymbolDecimalsService,
        private chartDatafeed: ChartDatafeed,
        private chartSecurityService: ChartSecurityService,

        @Optional() // [HED-930] Not used in DEX
        private orderPlacingService: OrderPlacingService,

        private renderer: Renderer2,
        private cellSizeObserverService: CellSizeObserverService,
        private tradingViewChartFactory: TradingViewChartFactory,

        @Optional() // [HED-930] Not used in DEX
        private accountService: AccountService
    ) {}

    public ngOnInit(): void {
        this.listenToWidgetSettingsUpdate();
    }

    public ngOnDestroy(): void {
        // Support for Language decorator.
    }

    public ngAfterViewInit(): void {
        this.chartStyleVariables = new TradingViewChartStyleVariablesAccessor(this.chartContainerElementRef);
        this.chartLineStyles = this.chartStyleVariables.getMapOfStrings(null, CHART_LINE_STYLE_VARIABLE_NAMES);

        const timeZoneName: Timezone = getTimeZoneNameForChart();
        const settings = this.widget.implementationInstance.settings;
        const securityKey: SecurityKey = settings.securityKey;
        const securityKeyStr = formatSecurityKey(securityKey);
        const candleSize = settings.candleSize as ResolutionString;

        const options: ChartingLibraryWidgetOptions = {
            container_id: this.chartId,
            locale:       'en',
            symbol:       securityKeyStr,
            interval:     candleSize,

            /**
             * Valid chart internal timeframe is a number with a letter D for days and M for months.
             * Ref: https://github.com/tradingview/charting_library/wiki/Widget-Constructor#timeframe
             *
             * Set it to undefined, we keep in settings visibleRange only.
             */
            timeframe:    undefined,

            datafeed:     this.chartDatafeed,
            debug:        !this.appConfigService.appConfig.production,
            timezone:     timeZoneName,
            saved_data:   settings.chartState?.[securityKeyStr]
        };

        this.chartInstance = this.tradingViewChartFactory.getChartInstance(options, this.chartStyleVariables);

        this.chartInstance.onChartReady(() => {
            this.chartReady$.next(true);
            this.chartReady$.complete();
        });

        this.chartReady$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => this.onChartReady());

        this.chartInstance$.next(this.chartInstance);
    }

    private onChartReady(): void {
        this.isChartReady = true;

        const chartApi = this.chartInstance.chart();
        const settings = this.widget.implementationInstance.settings;

        this.destroy$.subscribe(() => this.chartInstance.remove());

        if (settings.keepVisibleTimeRange && settings.visibleTimeRange) {
            const [initTimeRangeFrom, initTimeRangeTo] = settings.visibleTimeRange;

            chartApi.setVisibleRange({
                from: initTimeRangeFrom,
                to:   initTimeRangeTo
            }, {
                applyDefaultRightMargin: true
            });
        }

        chartApi.onSymbolChanged().subscribe(null, () => {
            const securityKey = this.getChartSecurityKey();
            const settingsSecurityKey = this.widget.implementationInstance.settings.securityKey;

            // Check that we have securityKey stored for the symbolExt.
            if (securityKey && !(settingsSecurityKey && isSecurityKeysEqual(securityKey, settingsSecurityKey))) {
                this.widget.implementationInstance.updateSettings({
                    securityKey
                }).subscribe();
            }
        });

        chartApi.crossHairMoved((params: CrossHairMovedEventParams) => this.crossHairMoved$.next(params));

        this.chartInstance.subscribe('onAutoSaveNeeded', () =>
            this.chartInstance.save(state => this.saveChartState(state as any))
        );

        this.initChartContainerReferences();
        this.listenOnChartResize();

        // this.listenToOrderList(); // [HED-930] Not used in DEX
        // this.listenToPositions(); // [HED-930] Not used in DEX

        this.updateChartSymbolOnSettingsSymbolChanges();
    }

    private saveChartState(state: Record<string, unknown>): void {
        const settingsUpdate = updateChartSettingsByState(
            this.chartInstance,
            this.widget.implementationInstance.settings,
            state
        );

        if (settingsUpdate) {
            this.widget.implementationInstance.updateSettings(settingsUpdate).subscribe();
        }
    }

    private getChartSecurityKey(): SecurityKey {
        const symbolExt = this.chartInstance.chart().symbolExt();

        return this.chartSecurityService.getSecurityKeyBySymbolExt(symbolExt);
    }

    private updateChartSymbolOnSettingsSymbolChanges(): void {
        const isChartSymbolDifferent = (securityKey: SecurityKey) => {
            const currentChartSecurityKey = this.getChartSecurityKey();

            return !currentChartSecurityKey || !isSecurityKeysEqual(currentChartSecurityKey, securityKey);
        };

        const setChartSymbol = (securityKey: SecurityKey) => new Observable(subscriber =>
            this.chartInstance.chart()
                .setSymbol(formatSecurityKey(securityKey), () => {
                    subscriber.next(securityKey);
                    subscriber.complete();
                })
        );

        this.updateChartSymbolBySettings$.pipe(
            switchMapTo(this.chartReady$),
            map(() => this.widget.implementationInstance.settings.securityKey),

            // [DC-270] Wait for chart to complete the previous symbol changing process.
            waitPreviousProcessLast(securityKey => isChartSymbolDifferent(securityKey)
                ? setChartSymbol(securityKey)
                : EMPTY
            ),

            takeUntil(this.destroy$)
        ).subscribe();
    }

    private listenToWidgetSettingsUpdate(): void {
        this.widget.implementationInstance.security$.pipe(
            tap(security => {
                this.security = security;
                this.securityFormats = this.symbolDecimalsService.getSecurityFormats(security);
                this.securityInputParams = this.symbolDecimalsService.getSecurityInputParams(security);

                this.updateChartSymbolBySettings$.next();
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private listenToOrderList(): void {
        this.orderListService
            .configure({
                displayOrders: [],
                filterFn$:     this.createOrderFilterStream()
            })
            .orderListEvent$.pipe(
            handleOrderListEvents<DisplayOrder>({
                [OrderListEventType.DISPLAY_ORDER_CREATED_OR_UPDATED]:
                    event => this.createOrUpdateOrderLine(event.displayOrder),
                [OrderListEventType.DISPLAY_ORDER_DELETED]:
                    event => this.onOrderDeleted(event.displayOrder),
                [OrderListEventType.LIST_CLEARED]:
                    () => this.onOrderListCleared()
            })
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private listenToPositions(): void {
        this.widget.implementationInstance.security$.pipe(
            switchMap(security => this.listenToPositionStream(security)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private onOrderDeleted(displayOrder: DisplayOrder): void {
        const lineRef = this.orderLineMapByClOrdId.get(displayOrder.order.clOrdId);

        if (lineRef) {
            lineRef.chartLine.remove();
            this.orderLineMapByClOrdId.delete(displayOrder.order.clOrdId);
        }
    }

    private onOrderListCleared(): void {
        this.orderLineMapByClOrdId.forEach((lineRef => lineRef.chartLine.remove()));
        this.orderLineMapByClOrdId.clear();
    }

    private createOrUpdateOrderLine(displayOrder: DisplayOrder): OrderLineRef {
        const order: Order = displayOrder.order;
        const orderColor: string = this.chartLineStyles[CHART_LINE_COLOR_VARIABLE_BY_SIDE[order.side]];
        const orderTextColor: string = this.chartLineStyles[CHART_LINE_TEXT_COLOR_VARIABLE_BY_SIDE[order.side]];

        let lineRef = this.orderLineMapByClOrdId.get(order.clOrdId);

        const updateLineByOrder = () => {
            const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfoForOrder(lineRef.order);
            const qty = getQtyFromOrder(this.security, lineRef.order, qtyMultiCurrencyInfo);
            const qtyAssetType = getOrderQtyAssetType(this.security, lineRef.order, qtyMultiCurrencyInfo);

            lineRef.chartLine
                .setPrice(lineRef.order.price)
                .setQuantity(this.formatOrderQtyStr(qty, qtyMultiCurrencyInfo, qtyAssetType));
        };

        if (lineRef) {
            lineRef.order = order;
            updateLineByOrder();
        } else {
            const chart = this.chartInstance.activeChart();
            const chartLine = chart.createOrderLine({disableUndo: false});

            lineRef = {
                chartLine,
                order
            };

            updateLineByOrder();

            chartLine

                // Set values
                .setEditable(true)
                .setText(order.side)

                // Set actions
                .onCancel(() => this.onOrderCancel(lineRef.order))
                .onModify(() => this.createOrderDetailsWidget(lineRef.order))

                // Set styles
                .setLineColor(orderColor)
                .setBodyBorderColor(this.chartLineStyles.lineBodyBorderColor)
                .setBodyBackgroundColor(this.chartLineStyles.lineBodyBackgroundColor)
                .setBodyTextColor(orderTextColor)
                .setQuantityBorderColor(this.chartLineStyles.lineBodyBorderColor)
                .setQuantityBackgroundColor(this.chartLineStyles.lineBodyBackgroundColor)
                .setQuantityTextColor(orderTextColor)
                .setCancelButtonBorderColor(this.chartLineStyles.lineBodyBorderColor)
                .setCancelButtonBackgroundColor(this.chartLineStyles.lineBodyBackgroundColor)
                .setCancelButtonIconColor(orderTextColor);

            if (!IS_ORDER_REPLACE_DISABLED) {
                chartLine
                    .onMove(() => this.onOrderReplace(lineRef.order));
            }

            this.orderLineMapByClOrdId.set(order.clOrdId, lineRef);
        }

        return lineRef;
    }

    private createOrderFilterStream(): Observable<(order: Order) => boolean> {
        return this.widget.implementationInstance.security$.pipe(
            map(security =>
                (order: Order): boolean => isOrderActive(order) && isSecurityKeysEqual(order.security, security)
                    && !!order.price // Hide orders w/o price (it's mainly TWAP Market orders).
            )
        );
    }

    private listenToPositionStream(securityKey: SecurityKey): Observable<Position[]> {
        return getSquashedPositionsStream({
            positionsStreamingService: this.positionsStreamingService,
            filterBy:                  position => isSecurityKeysEqual(position.security, securityKey),
            onPosition:                position => this.updatePositionLine(position),
            onCleanUp:                 isNoFollowupPositions => {
                if (isNoFollowupPositions) {
                    this.removePositionLine();
                }
            }
        }).pipe(
            finalize(() => this.removePositionLine())
        );
    }

    private removePositionLine(): void {
        if (this.positionLine) {
            this.positionLine.remove();
            this.positionLine = null;
        }
    }

    private updatePositionLine(position: Position): void {
        this.removePositionLine();

        const isPositionVisible = PositionsStreamingService.isPositionCloseable(position)
            && !PositionsStreamingService.isPositionDeprecated(position);

        if (isPositionVisible) {
            const reportWithDecimalFormat: NumberFormat = this.securityFormats.reportWithDecimalFormat;
            const totalPnL: number = DisplayPositionFactory.getTotalPnL(position);
            const positionLabelTextColor: string = totalPnL > 0
                ? this.chartLineStyles.lineTextColorBuy
                : this.chartLineStyles.lineTextColorSell;

            const side: Side = position.quantity >= 0
                ? Side.BUY
                : Side.SELL;

            const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfoForPosition(position);

            const positionColor = this.chartLineStyles[CHART_LINE_COLOR_VARIABLE_BY_SIDE[side]];
            const positionTextColor = this.chartLineStyles[CHART_LINE_TEXT_COLOR_VARIABLE_BY_SIDE[side]];
            const positionBorderColor = this.chartLineStyles[CHART_LINE_TEXT_COLOR_VARIABLE_BY_SIDE[side]];

            this.formattedTotalPnL = reportWithDecimalFormat.format(totalPnL);

            this.positionLine = this.chartInstance.activeChart().createPositionLine({disableUndo: false})

                // Set values
                .setText(`${this.positionLabelText}: ${this.formattedTotalPnL}`)
                .setQuantity(this.formatOrderQtyStr(Math.abs(position.quantity), qtyMultiCurrencyInfo))
                .setPrice((position.averagePrice))

                // Set styles
                .setLineColor(positionColor)
                .setBodyBorderColor(positionBorderColor)
                .setBodyBackgroundColor(this.chartLineStyles.lineBodyBackgroundColor)
                .setBodyTextColor(positionLabelTextColor)
                .setQuantityBorderColor(positionBorderColor)
                .setQuantityBackgroundColor(this.chartLineStyles.lineBodyBackgroundColor)
                .setQuantityTextColor(positionTextColor)
                .setLineLength(50)
                .setLineStyle(2)
                .setLineWidth(1)
                .setCloseButtonBorderColor(positionBorderColor)
                .setCloseButtonBackgroundColor(this.chartLineStyles.lineBodyBackgroundColor)
                .setCloseButtonIconColor(positionTextColor);
        }
    }

    private onOrderCancel(order: Order): void {
        if (this.accountService.hasPermissionOnAccountId(order.accountId)) {
            this.submitOrder(order, OrderTransactionType.CANCEL);
        }
    }

    private onOrderReplace(order: Order): void {
        if (!IS_ORDER_REPLACE_DISABLED && this.accountService.hasPermissionOnAccountId(order.accountId)) {
            const lineRef = this.orderLineMapByClOrdId.get(order.clOrdId);

            const newOrder = {
                ...order,
                price: lineRef.chartLine.getPrice()
            };

            const handleActionAborted = () => lineRef.chartLine.setPrice(lineRef.order.price);

            this.submitOrder(newOrder, OrderTransactionType.REPLACE, handleActionAborted);
        }
    }

    private createOrderDetailsWidget(order: Order): void {
        const floatCellDef: FloatCellDefinition = {
            id:                null,
            pos:               [null, null],
            size:              [null, null],
            activeWidgetIndex: 0,
            widgets:           []
        };
        const widgetDef: WidgetDefinition = {
            id:           null,
            tabId:        this.widget.tabId,
            cellId:       null,
            widgetTypeId: ORDER_DETAILS_WIDGET_DESCRIPTION.widgetTypeId,
            settings:     {
                              title:          null,
                              clOrdId:        order.clOrdId,
                              securityKeyStr: formatSecurityKey(order.security)
                          } as OrderDetailsWidgetSettingsDef
        };

        this.floatLayoutService.createFloatCellByDefinition(floatCellDef, widgetDef, this.widget.cell);
    }

    private submitOrder(
        order: Order,
        orderTransactionType: OrderTransactionType,
        onActionAborted: () => void = () => null
    ): void {
        let isSubmitted: boolean = false;
        let action$: Observable<any> = EMPTY;

        const actionParams: OrderCreationParams & OrderCancelingParams = {
            security:                 this.security,
            accountBase:              getAccountBaseByNewOrderParams(order),
            confirmModalAnchorVcRef:  this.confirmModalAnchorViewContainerRef,
            isConfirmationDisabled:   !this.widget.implementationInstance.settings.isConfirmationEnabled,
            isOneClickTradingEnabled: true
        };

        if (orderTransactionType === OrderTransactionType.CANCEL) {
            action$ = this.orderPlacingService.initiateOrderCanceling(actionParams, order);
        } else if (orderTransactionType === OrderTransactionType.REPLACE) {
            action$ = this.initiateOrderReplacing(actionParams, order);
        }

        action$.pipe(
            tap(() => isSubmitted = true),
            finalize(() => {
                if (!isSubmitted) {
                    onActionAborted();
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            error: () => null // Hide any possible exceptions.
        });
    }

    private initiateOrderReplacing(actionParams: OrderCreationParams, order: Order): Observable<any> {
        const {ordType, orderQty, timeInForce, side, accountId, clOrdId} = order;
        const replaceOrderModel: ReplaceOrCancelOrder = {
            orderQty,
            timeInForce,
            side,
            transactionType:    OrderTransactionType.REPLACE,
            accountId,
            ordType,
            origClOrdId:        clOrdId,
            portfolioId:        order.portfolioId,
            investmentEntityId: order.investmentEntityId,
            leverage:           order.leverage
        };

        if (ordType !== OrdType.MARKET) {
            replaceOrderModel.price = ordType === OrdType.STOP
                ? order.stopPx
                : order.price;

            if (ordType === OrdType.STOP_LIMIT) {
                replaceOrderModel.stopPx = order.stopPx;
            }
        }

        return this.orderPlacingService.initiateOrderPlacing(
            actionParams,
            replaceOrderModel
        );
    }

    @Language()
    private onLanguageChange(): void {
        this.positionLabelText = this.translationService.translate(TOTAL_PNL_LABEL);

        if (this.positionLine) {
            this.positionLine.setText(`${this.positionLabelText}: ${this.formattedTotalPnL}`);
        }
    }

    private listenOnChartResize(): void {
        this.onResize(); // hide toolbars after chart creation, if it's needed
        this.resize$.pipe(
            tap(() => this.onResize()),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private onResize(): void {
        const rect: DOMRect = (this.chartContainerElementRef.nativeElement as Element).getBoundingClientRect();
        const chartApi = this.chartInstance.chart();

        if (rect.height < 630) {
            if (chartApi.getCheckableActionState('drawingToolbarAction')) {
                chartApi.executeActionById('drawingToolbarAction');
            }
        } else {
            if (!chartApi.getCheckableActionState('drawingToolbarAction')) {
                chartApi.executeActionById('drawingToolbarAction');
            }
        }

        if (rect.width < 380 || rect.height < 400) {
            if (this.isTopToolbarOverflow !== true) {
                this.isTopToolbarOverflow = true;
                this.toggleChartContainerHidingClass(TOP_TOOLBAR_HIDING_CLASS, this.isTopToolbarOverflow);
            }
        } else {
            if (this.isTopToolbarOverflow !== false) {
                this.isTopToolbarOverflow = false;
                this.toggleChartContainerHidingClass(TOP_TOOLBAR_HIDING_CLASS, this.isTopToolbarOverflow);
            }
        }
    }

    private toggleChartContainerHidingClass(className: string, state: boolean): void {
        const renderer = this.renderer;
        const element = this.chartDocument.body;

        if (state) {
            renderer.addClass(element, className);
        } else {
            renderer.removeClass(element, className);
        }
    }

    private initChartContainerReferences(): void {
        this.chartIFrame = this.chartContainerElementRef.nativeElement.querySelector('iframe');
        this.chartDocument = this.chartIFrame.contentDocument || this.chartIFrame.contentWindow.document;

        this.destroy$.subscribe(() => {
            this.chartIFrame = null;
            this.chartDocument = null;
        });
    }

    /**
     * Formats order/position quantity to string with currency (if needed).
     */
    private formatOrderQtyStr(
        qty: number,
        qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo,
        qtyAssetType?: AssetType
    ): string {
        const security = this.security;

        qtyAssetType = qtyAssetType || qtyMultiCurrencyInfo.baseQtyAssetType;

        const qtyAssetFormats = getOrderQtyCorrectedSecurityFormats({
            security,
            qtyMultiCurrencyInfo,
            securityInputParams:   this.securityInputParams,
            normalSecurityFormats: this.securityFormats,
            qtyAssetType
        });

        /**
         * Show qty currency only if it differs from base asset
         */
        return [
            qtyAssetFormats.quantityWithDecimalFormat.format(Math.abs(qty)),
            (qtyAssetType !== AssetType.BASE)
                ? getOrderQtyCurrency(security, qtyMultiCurrencyInfo, qtyAssetType)
                : ''
        ].filter(Boolean).join(' ');
    }

}
